/**
 * Pré-carregamento (cache em dois níveis, ver goes/frameCache.ts):
 *
 * - baixa TODOS os quadros do plano (nível comprimido), os mais próximos do
 *   quadro atual primeiro — a animação já pode tocar enquanto baixa. Isso só
 *   depois do primeiro play (ou exportando, `loopLoadAllowed`): antes, o
 *   portal está parado na medição mais recente e baixa só o quadro na tela
 *   (e os que o usuário pedir navegando), mantendo os que já baixou;
 * - mantém decodificada uma JANELA de quadros (tamanho pelo orçamento de
 *   pixels): o loop inteiro quando cabe; quando não, a maior parte fica fixa e
 *   só alguns passam pela decodificação a cada volta (`decodeWindow`). O
 *   degrau do bitmap é o mais nítido em que no máximo um quarto do loop passa
 *   (`loopDecodeWidth`);
 * - troca de qualidade (ex.: zoom na automática): se a largura antiga + a
 *   nova cabem no orçamento comprimido, a animação continua na antiga até a
 *   nova estar toda baixada e a janela decodificada (uma única troca, sem
 *   travar); se não cabem, troca JÁ para a nova e espera os quadros novos
 *   (sem nunca misturar larguras durante a reprodução);
 * - assim que a nova largura passa a ser a da animação, as larguras antigas
 *   (e versões "quadro parado" que não servem mais) saem do cache na hora —
 *   só a imagem que está na tela fica até ser substituída.
 */
import { decodeWidthFor, decodeWindowSize, detectDeviceProfile, loopDecodeWidth, type DeviceProfile } from '../goes/budget';
import { frameCache, type FrameCache, type FrameRequest } from '../goes/frameCache';
import { compressedBudget } from '../goes/frameStorage';
import type { ImageWidth } from '../goes/star';
import { subscribeSelector } from '../lib/store';
import { useTimeStore } from '../state/time';
import { showToast } from '../state/toasts';
import { useViewStore } from '../state/view';
import { sectorCssPixels } from './controller';
import { decodeWindow } from './frames';
import { activePlanLayers, currentPlayWidth, layerUrlAt, usePlanStore, widthFromUrl, type PlanProgress } from './plan';
import { getPlaybackDirection } from './playback';
import { loopLoadAllowed, useLoopStore } from './store';

/** Espera no mesmo quadro antes de buscar a versão mais nítida (evita baixar ao passar quadros). */
const STILL_DELAY_MS = 700;
/** Espera depois de mexer no zoom antes de SUBIR a largura de decodificação (7200 px). */
const DECODE_GROW_DELAY_MS = 600;
/** Vista parada por este tempo antes de DESCER a largura de decodificação (evita ir e voltar). */
const DECODE_SHRINK_DELAY_MS = 2000;

/** Ordem das larguras de bitmap: natural (null) é a maior. */
const decodeRank = (w: number | null) => (w === null ? Infinity : w);

const frameGates = new Set<(i: number) => boolean>();

/**
 * Frações do orçamento de pixels decodificados reservadas a outras camadas,
 * por dono (o radar, o detalhe em blocos). O total fica em até
 * `MAX_RESERVED`: o satélite sempre guarda ao menos 15% para a base.
 */
const reservations = new Map<string, number>();
const MAX_RESERVED = 0.85;
let reservedFraction = 0;
const reserveListeners = new Set<() => void>();

function updateReserved(): void {
  let total = 0;
  for (const f of reservations.values()) total += f;
  reservedFraction = Math.min(MAX_RESERVED, total);
  reserveListeners.forEach((l) => l());
}

/**
 * Reserva `fraction` do orçamento de pixels decodificados do aparelho para
 * outra camada animada — o radar reserva o que usa (5% a 25%), o detalhe em
 * blocos também (até 60%): a janela do satélite encolhe na mesma proporção, e
 * o total não passa do orçamento. Chamar de novo com o mesmo `owner` substitui
 * a reserva dele. Retorna a liberação.
 */
export function reservePixelBudget(fraction: number, owner = 'radar'): () => void {
  reservations.set(owner, Math.max(0, Math.min(0.6, fraction)));
  updateReserved();
  return () => {
    reservations.delete(owner);
    updateReserved();
  };
}

/**
 * Registra outra camada animada que a reprodução deve esperar (ex.: o radar,
 * com cache próprio): `gate(i)` diz se o quadro `i` dela já está pronto para
 * desenhar. Retorna o cancelamento.
 */
export function registerFrameGate(gate: (i: number) => boolean): () => void {
  frameGates.add(gate);
  return () => {
    frameGates.delete(gate);
  };
}

/**
 * O quadro `i` está DECODIFICADO NA LARGURA EM QUE A ANIMAÇÃO TOCA em todas as
 * camadas visíveis (ou falhou de vez)? Sem substitutos de outra largura: é o
 * que evita a animação alternar nitidez. As camadas registradas com
 * `registerFrameGate` também precisam estar prontas.
 */
export function isFrameReady(i: number, cache: FrameCache = frameCache): boolean {
  const { plan } = usePlanStore.getState();
  const width = currentPlayWidth();
  if (!plan || !width) return false;
  for (const layer of activePlanLayers(plan)) {
    const url = layerUrlAt(layer, i, width);
    if (!url) continue;
    const st = cache.status(url);
    if (st !== 'ready' && st !== 'error') return false;
  }
  for (const gate of frameGates) if (!gate(i)) return false;
  return true;
}

/**
 * O quadro `i` não tem imagem em NENHUMA camada visível (todas falharam de
 * vez)? A reprodução pula esses quadros para a animação não piscar vazia; com
 * o loop parado o usuário ainda pode ir até eles (e vê o aviso).
 */
export function isFrameUnavailable(i: number, cache: FrameCache = frameCache): boolean {
  const { plan } = usePlanStore.getState();
  const width = currentPlayWidth();
  if (!plan || !width) return false;
  let any = false;
  for (const layer of activePlanLayers(plan)) {
    const url = layerUrlAt(layer, i, width);
    if (!url) continue;
    any = true;
    if (cache.status(url) !== 'error') return false;
  }
  return any;
}

export interface PlayWidthInput {
  /** Largura em que a animação toca hoje (null = ainda nenhuma). */
  playWidth: ImageWidth | null;
  /** Largura do plano (a baixar). */
  target: ImageWidth;
  /** Bytes já baixados da largura antiga (que ficariam durante a troca). */
  oldBytes: number;
  /** Bytes do loop inteiro na nova largura. */
  newBytes: number;
  budgetBytes: number;
  /** A nova largura já está toda baixada e a janela decodificada? */
  targetReady: boolean;
  /**
   * A largura antiga tem o loop inteiro baixado? Se não (ex.: portal parado,
   * só o quadro da tela baixado), seguir nela obrigaria a baixar o loop nas
   * DUAS larguras — troca já. Ausente = sim.
   */
  oldComplete?: boolean;
}

/**
 * Política de troca de largura:
 * - `keep-old`: antiga + nova cabem no orçamento → a animação segue na antiga
 *   até a nova ficar pronta (troca única, sem travar);
 * - `switch-now`: não cabem → troca já para a nova (a antiga é descartada; a
 *   reprodução espera os quadros novos).
 */
export function transitionPolicy(input: Pick<PlayWidthInput, 'oldBytes' | 'newBytes' | 'budgetBytes'>): 'keep-old' | 'switch-now' {
  return input.oldBytes + input.newBytes <= input.budgetBytes ? 'keep-old' : 'switch-now';
}

/** Largura em que a animação deve tocar agora. */
export function decidePlayWidth(input: PlayWidthInput): ImageWidth {
  const { playWidth, target, targetReady } = input;
  if (playWidth === null || playWidth === target) return target;
  if (targetReady || input.oldComplete === false) return target;
  return transitionPolicy(input) === 'keep-old' ? playWidth : target;
}

function computeProgress(cache: FrameCache): PlanProgress {
  const { plan } = usePlanStore.getState();
  const seen = new Set<string>();
  const p: PlanProgress = { downloaded: 0, decoded: 0, failed: 0, total: 0, bytesDownloaded: 0, bytesTotal: plan?.bytesTotal ?? 0 };
  for (const layer of activePlanLayers(plan)) {
    layer.urls.forEach((url, i) => {
      if (!url || seen.has(url)) return;
      seen.add(url);
      const st = cache.status(url);
      if (st === 'error') p.failed++;
      else if (cache.isDownloaded(url)) {
        p.downloaded++;
        if (st === 'ready') p.decoded++;
        const b = cache.bytes(url);
        p.bytesDownloaded += Number.isFinite(b) ? b : (layer.sizes[i]?.[widthFromUrl(url)] ?? 0);
      }
    });
  }
  p.total = seen.size;
  return p;
}

/** Liga o pré-carregamento. Retorna a função de desligar. */
export function startPreloader(cache: FrameCache = frameCache, fullProfile: DeviceProfile = detectDeviceProfile()): () => void {
  // Orçamento de pixels descontada a reserva de outras camadas (ver `reservePixelBudget`).
  // Arquivos no disco: o orçamento comprimido é o do disco (frameStorage.ts).
  const budgeted = (): DeviceProfile => ({
    ...fullProfile,
    budgetPixels: fullProfile.budgetPixels * (1 - reservedFraction),
    budgetBytes: compressedBudget(fullProfile),
  });
  let profile = budgeted();
  let stillArmed = false;
  let stillTimer: ReturnType<typeof setTimeout> | null = null;
  let lastFailed = 0;
  /** Largura de bitmap em uso por largura de arquivo (histerese do zoom). */
  const decodeWidths = new Map<ImageWidth, number | null>();
  /** A vista assentou (600 ms) — pode subir de degrau; parada (2 s) — pode descer. */
  let growAllowed = true;
  let shrinkAllowed = true;
  const decodeWidthFor2 = (
    width: ImageWidth,
    sector: number | null,
    canResize: boolean,
    budgetPixels: number,
    frameCount: number,
    layerCount: number,
  ): number | null => {
    // Qualidade escolhida à mão ("Máxima", "Alta"…): o que a tela mostra,
    // inteiro no zoom — quem escolheu aceita o processador. Automática: o
    // equilíbrio entre nitidez e processador (`loopDecodeWidth`).
    const fixed = useLoopStore.getState().quality !== 'auto';
    const wanted = !canResize
      ? null
      : fixed
        ? decodeWidthFor(width, sector, profile)
        : loopDecodeWidth(width, sector, profile, frameCount, layerCount, budgetPixels);
    const current = decodeWidths.get(width);
    const up = current !== undefined && decodeRank(wanted) > decodeRank(current);
    const down = current !== undefined && decodeRank(wanted) < decodeRank(current);
    if (current === undefined || (up && growAllowed) || (down && shrinkAllowed)) {
      decodeWidths.set(width, wanted);
      return wanted;
    }
    return current;
  };

  const sync = () => {
    profile = budgeted();
    const { plan, stillWidth } = usePlanStore.getState();
    if (!plan || plan.frames.length === 0) {
      cache.sync([]);
      updateProgress();
      return;
    }
    const target = plan.width;
    const { index } = useTimeStore.getState();
    const { playing, mode, exportLocked } = useLoopStore.getState();
    // Antes do primeiro play: só o quadro na tela (e o que já está baixado).
    const loadAll = loopLoadAllowed();
    const layers = activePlanLayers(plan);
    const n = plan.frames.length;
    const prevPlayWidth = usePlanStore.getState().playWidth;
    // Largura dos BITMAPS (só reduz o arquivo de 7200 px ao que a tela pede; ver
    // budget.ts). Sem redução no navegador, a janela é dimensionada pelo tamanho natural.
    const sector = sectorCssPixels();
    const canResize = cache.resizeSupported() !== false;
    // Numa troca de largura cada uma fica com metade do orçamento (abaixo).
    const maybeTransition = prevPlayWidth !== null && prevPlayWidth !== target;
    const targetDecodeWidth = decodeWidthFor2(target, sector, canResize, maybeTransition ? profile.budgetPixels / 2 : profile.budgetPixels, n, layers.length);

    // Decide a largura da animação ANTES de montar os pedidos: numa troca
    // imediata, a largura antiga já sai do cache neste mesmo `sync`.
    let playWidth: ImageWidth = target;
    if (prevPlayWidth !== null && prevPlayWidth !== target) {
      const oldUrls = layers.flatMap((l) => l.times.map((_, i) => layerUrlAt(l, i, prevPlayWidth)));
      const oldBytes = oldUrls.reduce((sum, u) => (u && cache.isDownloaded(u) ? sum + (cache.bytes(u) || 0) : sum), 0);
      // Durante a troca, cada largura usa metade do orçamento de pixels (o total não passa dele).
      const targetWindow = decodeWindow(
        index,
        n,
        decodeWindowSize(profile.budgetPixels / 2, target, layers.length, targetDecodeWidth),
        { playing, mode, direction: getPlaybackDirection() },
      );
      const done = (u: string | null) => !u || cache.isDownloaded(u) || cache.status(u) === 'error';
      const ready = (u: string | null) => !u || cache.status(u) === 'ready' || cache.status(u) === 'error';
      const oldComplete = oldUrls.every(done);
      const targetReady =
        layers.every((l) => l.urls.every(done)) && layers.every((l) => targetWindow.every((i) => ready(l.urls[i])));
      playWidth = decidePlayWidth({
        playWidth: prevPlayWidth,
        target,
        oldBytes,
        newBytes: plan.bytesTotal,
        budgetBytes: profile.budgetBytes,
        targetReady,
        oldComplete,
      });
    }
    if (playWidth !== prevPlayWidth) usePlanStore.setState({ playWidth });

    const transitioning = playWidth !== target;
    // Orçamento de pixels: todo para a largura da animação; na troca, metade
    // para a antiga (que toca) e metade para a nova (pré-decodificada).
    const pixelBudget = transitioning ? profile.budgetPixels / 2 : profile.budgetPixels;
    const playDecodeWidth = decodeWidthFor2(playWidth, sector, canResize, pixelBudget, n, layers.length);
    const windowSize = decodeWindowSize(pixelBudget, playWidth, layers.length, playDecodeWidth);
    if (usePlanStore.getState().decodeWindow !== windowSize) usePlanStore.setState({ decodeWindow: windowSize });
    const bitmapWidth = playDecodeWidth ?? playWidth;
    if (usePlanStore.getState().bitmapWidth !== bitmapWidth) usePlanStore.setState({ bitmapWidth });
    const direction = getPlaybackDirection();
    const window = decodeWindow(index, n, windowSize, { playing, mode, direction });
    const targetWindow = transitioning
      ? decodeWindow(index, n, decodeWindowSize(profile.budgetPixels / 2, target, layers.length, targetDecodeWidth), {
          playing,
          mode,
          direction,
        })
      : [];
    const requests: FrameRequest[] = [];

    // Parado: o quadro atual volta ao tamanho natural (nitidez máxima ao olhar de perto).
    const stillNatural = stillArmed && !playing && !exportLocked;
    for (const layer of layers) {
      const req = (url: string | null, i: number, priority: number, decode: number | null, natural = false) => {
        const t = layer.times[i];
        if (!url || t === null || t === undefined) return;
        const width = widthFromUrl(url);
        const decodeWidth = decode === null || natural ? null : decodeWidthFor2(width, sector, canResize, pixelBudget, n, layers.length);
        requests.push({ url, productId: layer.productId, time: t, width, priority, decode, decodeWidth });
      };
      // Com o loop ainda não liberado, um quadro entra só se é o atual ou já
      // foi baixado — assim o que foi visto não sai do cache, e arrastar a
      // barra cancela os pedidos dos quadros por onde só se passou.
      const inScope = (url: string | null, i: number) => loadAll || i === index || (!!url && cache.isDownloaded(url));
      // Nível 1: todos os quadros na largura do plano, do atual em diante.
      for (let k = 0; k < n; k++) {
        const i = (index + k) % n;
        if (!inScope(layer.urls[i], i)) continue;
        req(layer.urls[i], i, k, null);
        // Troca "keep-old": a largura antiga que já foi baixada fica até a nova estar pronta.
        if (transitioning) {
          const old = layerUrlAt(layer, i, playWidth);
          if (old && cache.isDownloaded(old)) req(old, i, 1e5 + k, null);
        }
      }
      // Nível 2: janela decodificada na largura em que a animação toca
      // (e, durante a troca, também na nova, para trocar sem travar).
      window.forEach((i, p) => {
        const url = layerUrlAt(layer, i, playWidth);
        if (inScope(url, i)) req(url, i, p, p);
      });
      targetWindow.forEach((i, p) => {
        if (inScope(layer.urls[i], i)) req(layer.urls[i], i, p, 100 + p);
      });
      // Quadro parado mais nítido (fora disso, a versão "still" sai do cache);
      // sem largura maior a buscar, o quadro atual é redecodificado no tamanho natural.
      if (stillWidth && stillArmed && !playing) req(layerUrlAt(layer, index, stillWidth), index, -1, -1);
      else if (stillNatural) req(layerUrlAt(layer, index, playWidth), index, -1, -1, true);
    }
    // O que não foi pedido (larguras antigas, stills que não servem) é descartado
    // já — exceto a imagem na tela, que sai assim que for substituída.
    cache.sync(requests);
    updateProgress();
  };

  let syncQueued = false;
  const scheduleSync = () => {
    if (syncQueued) return;
    syncQueued = true;
    queueMicrotask(() => {
      syncQueued = false;
      sync();
    });
  };

  const updateProgress = () => {
    const next = computeProgress(cache);
    const prev = usePlanStore.getState().progress;
    if (
      next.downloaded !== prev.downloaded ||
      next.decoded !== prev.decoded ||
      next.failed !== prev.failed ||
      next.total !== prev.total ||
      next.bytesDownloaded !== prev.bytesDownloaded ||
      next.bytesTotal !== prev.bytesTotal
    ) {
      usePlanStore.setState({ progress: next });
    }
    // Sem conexão as falhas são esperadas (o aviso de offline já aparece).
    if (next.failed > lastFailed && navigator.onLine !== false) {
      showToast({
        id: 'frames-failed',
        kind: 'warning',
        timeoutMs: 12_000,
        message:
          next.failed === 1
            ? 'Uma imagem não carregou. Ela será tentada de novo na próxima atualização.'
            : `${next.failed} imagens não carregaram. Elas serão tentadas de novo na próxima atualização.`,
        action: { label: 'Tentar agora', onClick: () => cache.retryFailed() },
      });
    }
    lastFailed = next.failed;
  };

  const rearmStill = () => {
    stillArmed = false;
    if (stillTimer) clearTimeout(stillTimer);
    stillTimer = null;
    if (!useLoopStore.getState().playing) {
      stillTimer = setTimeout(() => {
        stillArmed = true;
        sync();
      }, STILL_DELAY_MS);
    }
  };

  let growTimer: ReturnType<typeof setTimeout> | null = null;
  let shrinkTimer: ReturnType<typeof setTimeout> | null = null;
  const unsubs = [
    subscribeSelector(usePlanStore, (s) => s.plan, sync),
    // Zoom: a largura de decodificação do 7200 pode mudar (em degraus) —
    // subir logo que a vista assenta; descer só depois de parada um tempo.
    subscribeSelector(useViewStore, (s) => s.resolution, () => {
      growAllowed = false;
      shrinkAllowed = false;
      if (growTimer) clearTimeout(growTimer);
      if (shrinkTimer) clearTimeout(shrinkTimer);
      growTimer = setTimeout(() => {
        growTimer = null;
        growAllowed = true;
        sync();
      }, DECODE_GROW_DELAY_MS);
      shrinkTimer = setTimeout(() => {
        shrinkTimer = null;
        shrinkAllowed = true;
        sync();
      }, DECODE_SHRINK_DELAY_MS);
    }),
    subscribeSelector(usePlanStore, (s) => s.stillWidth, () => {
      rearmStill();
      sync();
    }),
    subscribeSelector(useTimeStore, (s) => s.index, () => {
      rearmStill();
      sync();
    }),
    // `loopArmed`/`exportLocked`: o primeiro play (ou a exportação) libera o loop inteiro.
    subscribeSelector(useLoopStore, (s) => `${s.playing}|${s.mode}|${loopLoadAllowed(s)}`, () => {
      rearmStill();
      scheduleSync();
    }),
    cache.subscribe(scheduleSync),
    (() => {
      reserveListeners.add(scheduleSync);
      return () => reserveListeners.delete(scheduleSync);
    })(),
  ];
  sync();
  rearmStill();

  return () => {
    unsubs.forEach((u) => u());
    if (stillTimer) clearTimeout(stillTimer);
    if (growTimer) clearTimeout(growTimer);
    if (shrinkTimer) clearTimeout(shrinkTimer);
  };
}
